import { PowerSlideSurface } from "./PowerSlideTemplates";

/**
 * Text colour is a choice of two, never a free colour. The theme defines one
 * light ink and one dark ink; each surface picks the one that reads on it.
 * This is what makes "full control over colour" and "cannot produce an
 * unreadable slide" compatible.
 */
export type PowerSlideInk = "light" | "dark";

export interface PowerSlideSurfaceTheme {
  background: string;
  ink: PowerSlideInk;
}

/**
 * A deck's colour, stored once per training. Slides carry no styling, so this
 * is the only place appearance lives - which is why a slide pasted between
 * decks needs no conversion.
 *
 * `accent` is deliberately not a surface. It is highlights: rules, dots,
 * numerals, stat figures and asset borders. A brand colour that sings at 4px
 * can be unreadable full-bleed, so it must be settable independently.
 */
export interface PowerSlideTheme {
  /** Which preset this came from, or "custom" once a colour is hand-edited. */
  presetId: string;
  surfaces: Record<PowerSlideSurface, PowerSlideSurfaceTheme>;
  accent: string;
  inkLight: string;
  inkDark: string;
}

const INK_LIGHT = "#ffffff";
const INK_DARK = "#272d37";

/**
 * The contrast each surface must clear, set by the *smallest* text that surface
 * actually carries. WCAG allows 3:1 for large text (24px regular or 18.66px
 * bold) and requires 4.5:1 otherwise:
 *
 * - `content` carries 18px regular captions, so 4.5.
 * - `title` carries nothing smaller than a 28px regular subtitle, so 3.
 * - `section` carries an 18px semibold eyebrow - under the 18.66px bold
 *   threshold, so still 4.5.
 *
 * A test keeps these in step with the templates rather than trusting the note.
 */
export const POWER_SLIDE_MINIMUM_CONTRAST: Record<PowerSlideSurface, number> = {
  content: 4.5,
  title: 3,
  section: 4.5,
};

function channelLuminance(channel: number): number {
  const value = channel / 255;
  return value <= 0.03928
    ? value / 12.92
    : Math.pow((value + 0.055) / 1.055, 2.4);
}

export function parseHexColor(
  hex: string
): { r: number; g: number; b: number } | undefined {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return undefined;
  const digits =
    match[1].length === 3
      ? match[1]
          .split("")
          .map((character) => character + character)
          .join("")
      : match[1];
  return {
    r: parseInt(digits.slice(0, 2), 16),
    g: parseInt(digits.slice(2, 4), 16),
    b: parseInt(digits.slice(4, 6), 16),
  };
}

export function relativeLuminance(hex: string): number {
  const rgb = parseHexColor(hex);
  if (!rgb) return 0;
  return (
    0.2126 * channelLuminance(rgb.r) +
    0.7152 * channelLuminance(rgb.g) +
    0.0722 * channelLuminance(rgb.b)
  );
}

/** WCAG contrast ratio, 1 to 21. */
export function contrastRatio(foreground: string, background: string): number {
  const a = relativeLuminance(foreground);
  const b = relativeLuminance(background);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

export function inkColor(theme: PowerSlideTheme, ink: PowerSlideInk): string {
  return ink === "light" ? theme.inkLight : theme.inkDark;
}

/**
 * How far a caption is lifted off full ink where the background allows it in
 * full. Captions read as quieter than body copy in print and in deck templates
 * generally, and that convention is worth keeping - but a caption is also the
 * smallest text on a slide, so this is a target rather than a guarantee. See
 * resolveCaptionAlpha for what actually renders.
 */
export const POWER_SLIDE_CAPTION_ALPHA = 0.72;

/** The opaque colour a translucent foreground actually resolves to. */
export function compositeOver(
  foreground: string,
  background: string,
  alpha: number
): string {
  const front = parseHexColor(foreground);
  const back = parseHexColor(background);
  if (!front || !back) return foreground;
  const blend = (a: number, b: number): string =>
    Math.round(a * alpha + b * (1 - alpha))
      .toString(16)
      .padStart(2, "0");
  return `#${blend(front.r, back.r)}${blend(front.g, back.g)}${blend(
    front.b,
    back.b
  )}`;
}

/**
 * How far a caption may be dimmed on a given background: as far as the
 * convention asks, or as far as the background allows, whichever is less.
 *
 * Fixing the tint instead would cost six of the eighteen swatches - every
 * mid-tone, where neither ink clears 4.5:1 once softened - so the tint yields
 * rather than the palette. A white slide gets the full print-style dimming; a
 * tight background quietly gets less of it, and full ink is always reachable,
 * which is what lets `availableInks` keep testing the ink alone.
 */
export function resolveCaptionAlpha(
  ink: string,
  background: string,
  minimum: number
): number {
  for (
    let alpha = POWER_SLIDE_CAPTION_ALPHA;
    alpha < 1;
    alpha = Math.round((alpha + 0.02) * 100) / 100
  ) {
    if (contrastRatio(compositeOver(ink, background, alpha), background) >= minimum) {
      return alpha;
    }
  }
  return 1;
}

/**
 * The ink choices that are legible on a surface. The panel offers only these,
 * so an author picks by looking rather than being told off afterwards - a
 * contrast error message in a theming flow means the model is wrong.
 */
export function availableInks(
  theme: PowerSlideTheme,
  role: PowerSlideSurface
): PowerSlideInk[] {
  const background = theme.surfaces[role].background;
  const minimum = POWER_SLIDE_MINIMUM_CONTRAST[role];
  // Testing the ink alone is sufficient because the caption tint yields to the
  // background rather than the other way round - see resolveCaptionAlpha.
  return (["light", "dark"] as PowerSlideInk[]).filter(
    (ink) => contrastRatio(inkColor(theme, ink), background) >= minimum
  );
}

/** True when every surface's chosen ink clears its own bar. */
export function isPowerSlideThemeLegible(theme: PowerSlideTheme): boolean {
  return (Object.keys(theme.surfaces) as PowerSlideSurface[]).every((role) =>
    availableInks(theme, role).includes(theme.surfaces[role].ink)
  );
}

/**
 * The ink that reads best on a background, used when a surface colour changes
 * and the current choice no longer clears its bar.
 */
export function bestInkFor(
  theme: PowerSlideTheme,
  background: string
): PowerSlideInk {
  return contrastRatio(theme.inkLight, background) >=
    contrastRatio(theme.inkDark, background)
    ? "light"
    : "dark";
}

function preset(
  presetId: string,
  content: string,
  title: string,
  section: string,
  accent: string,
  inks: { content: PowerSlideInk; title: PowerSlideInk; section: PowerSlideInk }
): PowerSlideTheme {
  return {
    presetId,
    surfaces: {
      content: { background: content, ink: inks.content },
      title: { background: title, ink: inks.title },
      section: { background: section, ink: inks.section },
    },
    accent,
    inkLight: INK_LIGHT,
    inkDark: INK_DARK,
  };
}

/**
 * Presets are the primary path, not a shortcut past the controls: the audience
 * is HR and L&D staff, who should choose by looking rather than configuring.
 * Every one of these is contrast-checked by a test.
 */
export const POWER_SLIDE_PRESETS: PowerSlideTheme[] = [
  preset("playit", "#ffffff", "#6c99b3", "#1c2a37", "#6c99b3", {
    content: "dark",
    title: "light",
    section: "light",
  }),
  preset("slate", "#ffffff", "#475569", "#0f172a", "#475569", {
    content: "dark",
    title: "light",
    section: "light",
  }),
  preset("forest", "#ffffff", "#3f6b52", "#17301f", "#4b8263", {
    content: "dark",
    title: "light",
    section: "light",
  }),
  preset("ember", "#fffaf5", "#b45309", "#3b2313", "#c2680f", {
    content: "dark",
    title: "light",
    section: "light",
  }),
  preset("plum", "#ffffff", "#6b4f7c", "#2a1c33", "#7d5c91", {
    content: "dark",
    title: "light",
    section: "light",
  }),
  preset("ink", "#f7f8f9", "#111827", "#000000", "#4b5563", {
    content: "dark",
    title: "light",
    section: "light",
  }),
];

export const DEFAULT_POWER_SLIDE_THEME = POWER_SLIDE_PRESETS[0];

export function getPowerSlidePreset(
  presetId: string
): PowerSlideTheme | undefined {
  return POWER_SLIDE_PRESETS.find((theme) => theme.presetId === presetId);
}

/** A theme on its own object, so a copied deck shares nothing with its source. */
export function clonePowerSlideTheme(theme: PowerSlideTheme): PowerSlideTheme {
  return {
    ...theme,
    surfaces: {
      content: { ...theme.surfaces.content },
      title: { ...theme.surfaces.title },
      section: { ...theme.surfaces.section },
    },
  };
}

function sameColours(a: PowerSlideTheme, b: PowerSlideTheme): boolean {
  return (
    a.accent.toLowerCase() === b.accent.toLowerCase() &&
    a.inkLight.toLowerCase() === b.inkLight.toLowerCase() &&
    a.inkDark.toLowerCase() === b.inkDark.toLowerCase() &&
    (Object.keys(a.surfaces) as PowerSlideSurface[]).every(
      (role) =>
        a.surfaces[role].background.toLowerCase() ===
          b.surfaces[role].background.toLowerCase() &&
        a.surfaces[role].ink === b.surfaces[role].ink
    )
  );
}

/**
 * A theme is only "a preset" while it still matches one exactly. Editing any
 * colour drops it to "custom", and editing back onto a preset picks the name up
 * again - so the field never claims a preset the deck no longer looks like.
 */
function retag(theme: PowerSlideTheme): PowerSlideTheme {
  const match = POWER_SLIDE_PRESETS.find((preset) =>
    sameColours(theme, preset)
  );
  return { ...theme, presetId: match ? match.presetId : "custom" };
}

/**
 * Every colour mutation goes through here. A change is only accepted if the
 * resulting theme is legible on every surface, so the guarantee is kept by
 * refusing the change rather than by warning about it afterwards.
 *
 * Switching ink is not enough on its own: a mid grey clears neither 4.5:1 bar,
 * light or dark, so some colours are simply unavailable for the content and
 * section surfaces. The panel filters its swatches with these same functions,
 * which is why an author never actually meets a refusal.
 */
function applyIfLegible(
  candidate: PowerSlideTheme,
  fallback: PowerSlideTheme
): PowerSlideTheme {
  return isPowerSlideThemeLegible(candidate) ? retag(candidate) : fallback;
}

export function setPowerSlideSurfaceBackground(
  theme: PowerSlideTheme,
  role: PowerSlideSurface,
  background: string
): PowerSlideTheme {
  if (!parseHexColor(background)) return theme;
  const withBackground: PowerSlideTheme = {
    ...theme,
    surfaces: {
      ...theme.surfaces,
      [role]: { ...theme.surfaces[role], background },
    },
  };
  const legal = availableInks(withBackground, role);
  // Keep the author's ink when it still reads; otherwise follow the colour.
  const candidate = legal.includes(withBackground.surfaces[role].ink)
    ? withBackground
    : {
        ...withBackground,
        surfaces: {
          ...withBackground.surfaces,
          [role]: {
            background,
            ink: legal[0] ?? bestInkFor(withBackground, background),
          },
        },
      };
  return applyIfLegible(candidate, theme);
}

export function setPowerSlideSurfaceInk(
  theme: PowerSlideTheme,
  role: PowerSlideSurface,
  ink: PowerSlideInk
): PowerSlideTheme {
  // Refuse an ink that cannot be read, rather than accepting it and warning.
  if (!availableInks(theme, role).includes(ink)) return theme;
  return retag({
    ...theme,
    surfaces: {
      ...theme.surfaces,
      [role]: { ...theme.surfaces[role], ink },
    },
  });
}

export function setPowerSlideAccent(
  theme: PowerSlideTheme,
  accent: string
): PowerSlideTheme {
  if (!parseHexColor(accent)) return theme;
  return retag({ ...theme, accent });
}

/**
 * Changing an ink can strand any surface that had chosen it, so every surface
 * is re-checked and moved to a legible choice if needed.
 */
export function setPowerSlideInkColor(
  theme: PowerSlideTheme,
  which: PowerSlideInk,
  color: string
): PowerSlideTheme {
  if (!parseHexColor(color)) return theme;
  const next: PowerSlideTheme = {
    ...theme,
    ...(which === "light" ? { inkLight: color } : { inkDark: color }),
  };
  const surfaces = { ...next.surfaces };
  (Object.keys(surfaces) as PowerSlideSurface[]).forEach((role) => {
    const legal = availableInks(next, role);
    if (legal.includes(surfaces[role].ink)) return;
    surfaces[role] = {
      ...surfaces[role],
      ink: legal[0] ?? bestInkFor(next, surfaces[role].background),
    };
  });
  return applyIfLegible({ ...next, surfaces }, theme);
}

/**
 * Whether a colour can be offered for a surface. The panel filters its swatches
 * with this, so a colour that would leave text unreadable is never presented -
 * the author picks from what works instead of being corrected.
 */
export function canSetPowerSlideSurfaceBackground(
  theme: PowerSlideTheme,
  role: PowerSlideSurface,
  background: string
): boolean {
  return setPowerSlideSurfaceBackground(theme, role, background) !== theme;
}

export function canSetPowerSlideInkColor(
  theme: PowerSlideTheme,
  which: PowerSlideInk,
  color: string
): boolean {
  return setPowerSlideInkColor(theme, which, color) !== theme;
}

/**
 * The curated swatches offered before the custom picker: neutrals, then darks,
 * then the brand-ish hues. Presets remain the primary path; these are the
 * second step, and a free hex field the third.
 */
export const POWER_SLIDE_SWATCHES: string[][] = [
  ["#ffffff", "#fffaf7", "#f7f8fa", "#eef1f5", "#dae0e6", "#b9c3cf"],
  ["#919ba7", "#5f6d7e", "#272d37", "#1c2a37", "#14202e", "#22303d"],
  ["#6c99b3", "#4a6fa5", "#4b8b6b", "#c4633a", "#8a5a9c", "#51829e"],
];

/**
 * Repairs a theme read from a training's metadata. Anything missing or
 * malformed falls back to the default rather than rendering a colourless deck.
 */
export function normalizePowerSlideTheme(value: unknown): PowerSlideTheme {
  if (!value || typeof value !== "object") return DEFAULT_POWER_SLIDE_THEME;
  const raw = value as Partial<PowerSlideTheme>;
  const surfaces = raw.surfaces ?? ({} as PowerSlideTheme["surfaces"]);

  const surfaceOr = (
    role: PowerSlideSurface
  ): PowerSlideSurfaceTheme => {
    const fallback = DEFAULT_POWER_SLIDE_THEME.surfaces[role];
    const candidate = surfaces[role];
    if (!candidate || !parseHexColor(candidate.background ?? "")) {
      return fallback;
    }
    return {
      background: candidate.background,
      ink: candidate.ink === "light" || candidate.ink === "dark"
        ? candidate.ink
        : fallback.ink,
    };
  };

  return {
    presetId: typeof raw.presetId === "string" ? raw.presetId : "custom",
    surfaces: {
      content: surfaceOr("content"),
      title: surfaceOr("title"),
      section: surfaceOr("section"),
    },
    accent: parseHexColor(raw.accent ?? "")
      ? (raw.accent as string)
      : DEFAULT_POWER_SLIDE_THEME.accent,
    inkLight: parseHexColor(raw.inkLight ?? "")
      ? (raw.inkLight as string)
      : INK_LIGHT,
    inkDark: parseHexColor(raw.inkDark ?? "")
      ? (raw.inkDark as string)
      : INK_DARK,
  };
}
